/* global navigator, setTimeout */

import { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
} from '@mui/material';
import { 
  ArrowBack,
  ContentCopy,
  Share,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import type { Customer } from "@/types";
import { useStoreSettings } from '../contexts/StoreSettingsContext';
import { useLocalization } from '@/contexts/LocalizationContext';

interface ReferFriendProps {
  onBack?: () => void;
  user: Customer;
}

export default function ReferFriend({ onBack, user: customer }: ReferFriendProps) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { storeSettings } = useStoreSettings();
  const { t } = useLocalization();
  const [copied, setCopied] = useState(false);

  const referralSettings = storeSettings?.referralSettings;

  const handleCopyCode = () => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(customer?.referralCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShareCode = () => {
    // Implement share functionality
    console.log('Share code');
  };

  const content = (
    <Box
      sx={{
        minHeight: isDesktop ? 'auto' : '100vh',
        backgroundColor: isDesktop ? 'transparent' : '#F8F9FA',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      {!isDesktop && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            backgroundColor: 'white',
            borderBottom: '1px solid #E0E0E0',
          }}
        >
          <IconButton onClick={onBack}>
            <ArrowBack />
          </IconButton>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton size="small">
              <Share />
            </IconButton>
            <Button
              variant="outlined"
              size="small"
              sx={{
                borderColor: '#A9C191',
                color: '#A9C191',
                borderRadius: '16px',
                textTransform: 'none',
                fontSize: '12px',
                px: 1,
              }}
            >
              EN
            </Button>
          </Box>
        </Box>
      )}

      {/* Main Content */}
      <Box sx={{ flex: 1, p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Title Section */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '24px',
              color: '#A9C191',
              mb: 1,
            }}
          >
            {t('referFriend.title')}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#666',
              fontWeight: 500,
            }}
          >
            {t('referFriend.subtitle')}
          </Typography>
        </Box>

        {/* Illustration */}
        <Box sx={{ textAlign: 'center', my: 2 }}>
          <img
            src="/images/img_gift_box.svg"
            alt="Gift Box"
            style={{
              width: '200px',
              height: '150px',
              objectFit: 'contain',
            }}
          />
        </Box>

        {/* Referral Code Box */}
        <Box
          sx={{
            backgroundColor: '#A9C191', borderRadius: '12px',
            p: 3,
            border: '1px solid #D9E4CE',
            position: 'relative',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              textAlign: 'center',
              color: 'white',
              mb: 1,
              fontSize: '18px',
              fontWeight: 500,
            }}
          >
            {t('referFriend.myReferralCode')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
            <Typography
              sx={{
                fontWeight: 700,
                color: 'white',
                fontSize:'32px',
                fontFamily: 'monospace',
              }}
            >
              {customer?.referralCode}
            </Typography>
            <IconButton
              onClick={handleCopyCode}
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: '#F6F9F4',
                },
              }}
            >
              <ContentCopy />
            </IconButton>
          </Box>
          {copied && (
            <Typography
              variant="caption"
              sx={{
                color: '#A9C191',
                position: 'absolute',
                bottom: '8px',
                right: '8px',
                fontWeight: 500,
              }}
            >
              {t('referFriend.copied')}
            </Typography>
          )}
        </Box>

        {/* Share Button */}
        <Button
          fullWidth
          variant="outlined"
          onClick={handleShareCode}
          sx={{
            borderColor: '#A9C191',
            color: '#666',
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 600,
            py: 1.5,
            '&:hover': {
              borderColor: '#94AF79',
              backgroundColor: '#F6F9F4',
            },
          }}
        >
          {t('referFriend.shareCode')}
        </Button>

        {/* Rewards Section */}
        {referralSettings?.enabled && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box
                sx={{
                  width: '4px',
                  height: '20px',
                  backgroundColor: '#A9C191',
                  borderRadius: '2px',
                  mr: 2,
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: '#333',
                }}
              >
                {t('referFriend.rewards.title')}
              </Typography>
            </Box>
            <Box sx={{ pl: 3 }}>
              <Typography
                variant="body2"
                sx={{
                  color: '#666',
                  mb: 1,
                  lineHeight: 1.6,
                }}
              >
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#666',
                  lineHeight: 1.6,
                }}
              >
                • {t('referFriend.rewards.noLimits')}
              </Typography>
            </Box>
          </Box>
        )}

        {/* How It Works Section */}
        {referralSettings?.enabled && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box
                sx={{
                  width: '4px',
                  height: '20px',
                  backgroundColor: '#A9C191',
                  borderRadius: '2px',
                  mr: 2,
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: '#333',
                }}
              >
                {t('referFriend.howItWorks.title')}
              </Typography>
            </Box>
            <Box sx={{ pl: 3 }}>
              <Typography
                variant="body2"
                sx={{
                  color: '#666',
                  lineHeight: 1.6,
                }}
              >
                {t('referFriend.howItWorks.step1')}
              </Typography>
            </Box>
            <Box sx={{ pl: 3, pt: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: '#666',
                  lineHeight: 1.6,
                }}
              >
                {t('referFriend.howItWorks.step2')}
              </Typography>
            </Box>
            <Box sx={{ pl: 3, pt: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: '#666',
                  lineHeight: 1.6,
                }}
              >
                {t('referFriend.howItWorks.step3')}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );

  // If it's desktop, wrap in background and dialog
  if (isDesktop) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          position: 'relative',
          background: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url("${storeSettings?.backgroundUrl || '/images/background.png'}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          py: 4,
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            width: '60%',
            maxWidth: '800px',
            backgroundColor: 'white',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          }}
        >
          {content}
        </Box>
      </Box>
    );
  }

  // If it's mobile, return the content directly
  return content;
}
